import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Shield, Zap, CheckCircle, ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import { PHONES, CONTACT, BLOG_POSTS } from '../content';
import { getDistrictBySlug, DISTRICT_PAGES } from '../data/districts';
import { SERVICE_PAGES } from '../data/services-seo';
import { NEIGHBORHOOD_PAGES } from '../data/neighborhoods';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';
import Breadcrumb from '../components/Breadcrumb';
import ETAIndicator from '../components/ETAIndicator';
import { SEOHead } from '../components/SEOHead';
import { DistrictSEOSchemas } from '../components/SEOHead';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

const TRUST_METRICS = [
  { icon: Zap, value: '30 dk', label: 'Ort. Müdahale', color: 'var(--red)' },
  { icon: Shield, value: '24 Ay', label: 'Garanti', color: 'var(--green)' },
  { icon: Phone, value: '3 Hat', label: 'Aktif Hat', color: 'var(--blue)' },
  { icon: Clock, value: '7/24', label: 'Kesintisiz', color: '#ff9500' },
];

export default function DistrictPage() {
  const location = require('react-router-dom').useLocation();
  const slug = location.pathname.replace('/', '');
  const district = getDistrictBySlug(slug);

  if (!district) return null;

  const nearbyDistricts = district.nearbyDistricts
    .map(ns => DISTRICT_PAGES.find(d => d.slug === ns))
    .filter(Boolean);

  return (
    <main data-testid={`district-page-${district.slug}`} style={{ background: 'var(--bg)' }}>
      <SEOHead
        path={`/${district.slug}`}
        title={district.title}
        description={district.metaDesc}
      />
      <DistrictSEOSchemas district={district} />

      {/* HERO */}
      <section style={{ background: 'var(--bg)', position: 'relative', overflow: 'hidden', paddingTop: 86 }}>
        <div style={{
          position: 'absolute', top: '30%', left: '10%',
          width: 600, height: 500,
          background: 'radial-gradient(ellipse, rgba(229,51,51,.08) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'var(--border)' }} />

        <div className="wrap" style={{ position: 'relative', zIndex: 1, padding: '3.5rem 1.5rem 3.5rem' }}>
          {/* Breadcrumb */}
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: 'Acil Yol Yardım', to: '/acil-yol-yardim' },
            { label: district.name },
          ]} />

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.75rem', marginBottom: '1.25rem' }}>
            <LiveBadge text={`${district.name} — Ekip Aktif`} />
          </div>

          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(2rem, 5.5vw, 3.5rem)', color: 'var(--text)',
            lineHeight: 1.05, letterSpacing: '-.04em', marginBottom: '.5rem',
          }}>
            {district.h1}
          </h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.4rem, 4vw, 2.5rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1.25rem',
          }}>
            {district.h1Accent}
          </p>
          <p style={{
            fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)',
            lineHeight: 1.72, maxWidth: 560, marginBottom: '2rem',
          }}>
            {district.intro}
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', marginBottom: '2rem' }}>
            <a href={PHONES.main_tel} data-testid="district-hero-call" className="btn btn-primary btn-xl ring num" style={{ gap: '.625rem' }}>
              <Phone size={17} /> Hemen Ara — {PHONES.main}
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="district-hero-whatsapp" data-gtm-action="whatsapp_click" className="btn btn-green" style={{ gap: '.5rem', padding: '.875rem 1.5rem', borderRadius: 'var(--rl)', fontSize: '.9375rem', fontWeight: 700 }}>
              {WA_ICON} WhatsApp ile Konum Gönder
            </a>
          </div>

          {/* ETA Indicator */}
          <ETAIndicator district={district.name} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginTop: '.75rem' }}>
            {['Yerinde tamir', '24 ay garanti', '7/24 aktif'].map(t => (
              <span key={t} style={{
                display: 'inline-flex', alignItems: 'center', gap: '.3rem',
                background: 'rgba(13,14,32,.7)', border: '1px solid rgba(28,31,60,.8)',
                borderRadius: 100, padding: '.25rem .75rem', fontSize: '.75rem', color: 'var(--text2)',
              }}>
                <span style={{ color: 'var(--green)', fontSize: '.65rem' }}>&#10003;</span> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST METRICS BAR */}
      <section style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }} className="district-trust-grid">
            {TRUST_METRICS.map(({ icon: Icon, value, label, color }, i) => (
              <div key={label} style={{
                padding: '1.75rem 1rem', borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.875rem',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 11,
                  background: `${color}18`, border: `1px solid ${color}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0,
                }}>
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1.25rem', color: 'var(--text)', lineHeight: 1, letterSpacing: '-.03em' }}>{value}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginTop: '.15rem' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .5 }}
            style={{
              maxWidth: 780,
              background: 'var(--card)', border: '1px solid var(--border)',
              borderRadius: 'var(--rl)', padding: 'clamp(1.5rem, 3vw, 2.5rem)',
            }}
          >
            <div
              className="district-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(district.content) }}
              style={{ color: 'var(--text2)', fontSize: '.925rem', lineHeight: 1.8 }}
            />
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      {district.faqs && district.faqs.length > 0 && (
        <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="wrap">
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <div className="pill" style={{ marginBottom: '1rem' }}>SSS</div>
              <h2 className="h2">
                {district.name} <span style={{ color: 'var(--blue)' }}>Sık Sorulan Sorular</span>
              </h2>
            </div>

            <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {district.faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  data-testid={`district-faq-${i}`}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: .4, delay: i * .08 }}
                  style={{
                    background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r)', padding: '1.5rem', transition: 'border-color .2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue-border)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <h3 style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                    fontSize: '.95rem', color: 'var(--text)', marginBottom: '.5rem',
                  }}>
                    {faq.q}
                  </h3>
                  <p style={{ fontSize: '.875rem', color: 'var(--text2)', lineHeight: 1.7 }}>
                    {faq.a}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEARBY DISTRICTS */}
      {nearbyDistricts.length > 0 && (
        <section className="section" style={{ background: 'var(--bg)' }}>
          <div className="wrap">
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
              <div className="pill pill-green" style={{ marginBottom: '1rem' }}>Yakın İlçeler</div>
              <h2 className="h2">
                Diğer <span style={{ color: 'var(--green)' }}>Hizmet Bölgelerimiz</span>
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem', maxWidth: 780, margin: '0 auto' }} className="nearby-grid">
              {nearbyDistricts.map((nd, i) => (
                <motion.div
                  key={nd.slug}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: .4, delay: i * .1 }}
                >
                  <Link
                    to={`/${nd.slug}`}
                    data-testid={`nearby-${nd.slug}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 'var(--r)', padding: '1.25rem 1.5rem',
                      transition: 'border-color .2s, transform .2s', textDecoration: 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.25rem' }}>
                        <MapPin size={14} style={{ color: 'var(--green)' }} />
                        <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.95rem', color: 'var(--text)' }}>
                          {nd.name}
                        </span>
                      </div>
                      <span style={{ fontSize: '.75rem', color: 'var(--text2)' }}>Lastik Yol Yardım</span>
                    </div>
                    <ArrowRight size={16} style={{ color: 'var(--muted)' }} />
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* All districts link */}
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link
                to="/acil-yol-yardim"
                data-testid="all-districts-link"
                style={{
                  fontSize: '.85rem', color: 'var(--blue)', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: '.35rem',
                  transition: 'opacity .2s',
                }}
              >
                Tüm ilçeleri gör <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* NEIGHBORHOOD LINKS */}
      {(() => {
        const districtHoods = NEIGHBORHOOD_PAGES.filter(n => n.districtSlug === district.slug);
        if (districtHoods.length === 0) return null;
        return (
          <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
            <div className="wrap">
              <h2 className="h2" style={{ marginBottom: '1.25rem' }}>
                {district.name} <span style={{ color: 'var(--green)' }}>Mahallelerimiz</span>
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                {districtHoods.map(n => (
                  <Link key={n.slug} to={`/${n.slug}`} style={{
                    display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 100, padding: '.35rem .9rem', fontSize: '.8rem', color: 'var(--text2)',
                    textDecoration: 'none', transition: 'border-color .2s, color .2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
                  >{n.name}</Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* GOOGLE MAPS */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap">
          <h2 className="h2" style={{ marginBottom: '1.25rem' }}>
            {district.name} <span style={{ color: 'var(--blue)' }}>Hizmet Bölgemiz</span>
          </h2>
          <div style={{
            borderRadius: 'var(--rl)', overflow: 'hidden',
            border: '1px solid var(--border)', height: 300,
          }}>
            <iframe
              data-testid="district-map"
              title={`${district.name} Lastik Yol Yardım Haritası`}
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(district.name + ' Kocaeli Türkiye')}&zoom=13`}
              width="100%" height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: '.5rem', textAlign: 'center' }}>
            {district.name} ve çevre bölgelere 7/24 mobil lastik hizmeti sunuyoruz.
          </p>
        </div>
      </section>

      {/* RELATED BLOG POSTS */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap">
          <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
            Faydalı <span style={{ color: 'var(--blue)' }}>Blog Yazıları</span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', maxWidth: 780, margin: '0 auto' }}>
            {BLOG_POSTS.slice(0, 3).map(blog => (
              <Link key={blog.id} to={`/blog/${blog.slug}`} className="blog-card" style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 'var(--rl)', overflow: 'hidden', textDecoration: 'none', display: 'block',
              }}>
                <div style={{ height: 130 }}>
                  <img src={blog.image} alt={blog.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                </div>
                <div style={{ padding: '.875rem' }}>
                  <span style={{ fontSize: '.68rem', color: 'var(--blue)', fontWeight: 600 }}>{blog.category}</span>
                  <h4 style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600,
                    fontSize: '.82rem', color: 'var(--text)', lineHeight: 1.4, marginTop: '.25rem',
                  }}>{blog.title}</h4>
                </div>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <Link to="/blog" style={{ fontSize: '.85rem', color: 'var(--blue)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '.35rem' }}>
              Tüm blog yazıları <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap">
          <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
            Sunduğumuz <span style={{ color: 'var(--green)' }}>Hizmetler</span>
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '.5rem' }}>
            {SERVICE_PAGES.map(s => (
              <Link key={s.slug} to={`/${s.slug}`} style={{
                display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 100, padding: '.35rem .9rem', fontSize: '.8rem', color: 'var(--text2)',
                textDecoration: 'none', transition: 'border-color .2s, color .2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
              >{s.h1}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCTA headline={`${district.name}'de Yolda Kalma.`} sub="Hemen Ara." />

      <style>{`
        .district-content h2 { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: clamp(1.3rem, 3vw, 1.75rem); color: var(--text); margin: 2rem 0 .75rem; line-height: 1.2; }
        .district-content h3 { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: clamp(1rem, 2vw, 1.25rem); color: var(--text); margin: 1.5rem 0 .5rem; }
        .district-content p { margin-bottom: 1rem; }
        .district-content ul { padding-left: 1.5rem; margin-bottom: 1.25rem; }
        .district-content li { margin-bottom: .4rem; color: var(--text2); }
        .district-content li::marker { color: var(--green); }
        .district-content strong { color: var(--text); font-weight: 600; }
        @media(max-width:768px) {
          .district-trust-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .nearby-grid { grid-template-columns: 1fr !important; }
        }
        @media(max-width:480px) {
          .district-trust-grid { grid-template-columns: 1fr !important; }
          .district-trust-grid > div { border-right: none !important; border-bottom: 1px solid var(--border); }
          .district-trust-grid > div:last-child { border-bottom: none; }
        }
      `}</style>
    </main>
  );
}
